/**
 * /picks — the board. Everyone who has picked this week, and how they're doing.
 *
 * The selection grid used to live at this URL and now lives at `/picks/edit`,
 * behind the button at the top. That swap follows the traffic: you fill a card
 * in once and then look at it a dozen times, so the thing you do repeatedly
 * should be the thing the tab opens on.
 *
 * `?week=` in the URL, arrows that step over switched-off weeks, and a row per
 * person that opens their card laid under your own.
 */

import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import type { PicksBoard as Board } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, GET } from '../lib/api'
import { Avatar, Body, Button, Card, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'
import { WeekSwitcher } from '../ui/WeekSwitcher'

const GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '40px minmax(0, 1fr) 56px 52px',
  gap: 10,
  alignItems: 'center',
}

export default function PicksBoard() {
  const { state } = useStore()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const weekParam = params.get('week')

  const [board, setBoard] = useState<Board | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const qs = weekParam ? `?week=${encodeURIComponent(weekParam)}` : ''
        setBoard(await GET<Board>(`/api/picks/board${qs}`, ac.signal))
        setErr(null)
      } catch (e) {
        if (ac.signal.aborted) return
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load the board.')
      }
    })()
    return () => ac.abort()
  }, [weekParam])

  const onWeek = useCallback((id: string) => {
    setBoard(null)
    setParams({ week: id }, { replace: true })
  }, [setParams])

  if (state?.user.optedOut) {
    return (
      <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Header bordered={false} title={<span className="screen-title">🎯 Picks</span>} />
        <Body>
          <Card>
            <div style={{ fontSize: 15, fontWeight: 600 }}>You’ve opted out of the community.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6, lineHeight: 1.5 }}>
              Your picks are still saved. Opt back in from Settings and they’re on the board again.
            </div>
            <Link to="/settings" style={{ display: 'inline-block', marginTop: 12, fontSize: 13, fontWeight: 700 }}>
              Settings →
            </Link>
          </Card>
        </Body>
      </Screen>
    )
  }

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        title={<span className="screen-title">🎯 Picks</span>}
        right={
          state && state.picks.total > 0 ? (
            <span className="mono" style={{ fontSize: 12, color: 'var(--dim)' }}>
              {state.picks.made} of {state.picks.total}
            </span>
          ) : undefined
        }
        sub={
          <div style={{ marginTop: 10 }}>
            <WeekSwitcher
              weeks={board?.weeks ?? []}
              weekId={board?.weekId ?? null}
              onChange={onWeek}
              label={board?.weekLabel}
            />
          </div>
        }
      />

      <Body style={{ padding: '10px var(--gutter) calc(24px + var(--nav-h))' }}>
        <Button onClick={() => navigate('/picks/edit')}>Add or edit picks</Button>
        <Spacer h={18} />

        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {!board ? (
          <><SkeletonCard lines={4} /><Spacer h={12} /><SkeletonCard lines={4} /></>
        ) : board.entries.length === 0 ? (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Nobody has picked this week.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              Someone has to go first.
            </div>
          </div>
        ) : (
          <div role="table" aria-label={`${board.weekLabel} picks`}>
            <div
              role="row"
              className="mono"
              style={{
                ...GRID, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase',
                color: 'var(--dim)', paddingBottom: 8,
              }}
            >
              <span role="columnheader" aria-label="Avatar" />
              <span role="columnheader" aria-label="Name" />
              <span role="columnheader" style={{ textAlign: 'right' }}>Made</span>
              <span role="columnheader" style={{ textAlign: 'right' }}>Pts</span>
            </div>

            {board.entries.map((e, i) => (
              <Link
                key={e.userId}
                role="row"
                to={`/picks/u/${encodeURIComponent(e.userId)}?week=${encodeURIComponent(board.weekId)}`}
                style={{
                  ...GRID, padding: '11px 0', minHeight: 56, color: 'var(--fg)',
                  borderTop: '1px solid var(--line)',
                  borderBottom: i === board.entries.length - 1 ? '1px solid var(--line)' : undefined,
                  background: e.isViewer ? 'var(--surf)' : undefined,
                }}
              >
                <Avatar initials={e.initials} size={34} self={e.isViewer} />
                <span
                  role="cell"
                  style={{
                    fontSize: 15, fontWeight: e.isViewer ? 700 : 500, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {e.displayName}{e.isViewer ? ' (me)' : ''}
                </span>
                <span
                  role="cell"
                  className="mono"
                  style={{ textAlign: 'right', fontSize: 13, color: 'var(--dim)' }}
                >
                  {e.made}/{board.total}
                </span>
                <span
                  role="cell"
                  className="mono"
                  style={{ textAlign: 'right', fontSize: 15, fontWeight: 700 }}
                >
                  {e.points}
                </span>
              </Link>
            ))}
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          <Link to="/picks/standings" style={{ fontSize: 13, fontWeight: 600 }}>
            Season standings →
          </Link>
        </div>
      </Body>
    </Screen>
  )
}
